import React, { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsCompareAggregateResponse, useSalesInsightsCompareAggregated } from '../../../../api/indicators';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardContext } from '../../DashboardContext';
import LineBarChartCompared, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChartCompared';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromComparedRanges } from '../../dashboardHelpers';

const ComparedSalesIncNetSalesExcTax = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [lineBarChartData, setLineBarChartData] = useState<LineBarChartDataItem[]>([]);
    const [lineBarChartDataCompared, setLineBarChartDataCompared] = useState<LineBarChartDataItem[]>([]);

    const initialStartDate = filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialEndDate = filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    const initialCompareStart = filters.comparePeriodFrom
        ? filters.comparePeriodFrom
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialCompareEnd = filters.comparePeriodTo
        ? filters.comparePeriodTo
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netSales,totalSales',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        compareStartDate: formatDateParam(initialCompareStart),
        compareEndDate: formatDateParam(initialCompareEnd),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromComparedRanges(
            initialStartDate,
            initialEndDate,
            initialCompareStart,
            initialCompareEnd
        )
    });

    const {
        data: salesInsightsAggregate,
        isLoading,
        isError,
        refetch: fetchSalesAndTransactions
    } = useSalesInsightsCompareAggregated<SalesInsightsCompareAggregateResponse>(params, 'compare-net-sales-insights-aggregate');

    useEffect(() => {
        const salesIncludingTaxLabel = t("screens.dashboarding.charts.salesIncludingTax");
        const netSalesExcludingTaxLabel = t("screens.dashboarding.charts.netSalesExcludingTax");
        if (salesInsightsAggregate?.salesInsightsAggregate) {
            setLineBarChartData(
                salesInsightsAggregate.salesInsightsAggregate.map((salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    [salesIncludingTaxLabel]: salesInsightsAggregateItem.salesInsights.totalSalesIncTax || 0,
                    [netSalesExcludingTaxLabel]: salesInsightsAggregateItem.salesInsights.netSalesExcTax || 0,
                    barData: salesInsightsAggregateItem.salesInsights.transactionVolume || 0
                }))
            );
        }
        if (salesInsightsAggregate?.comparedSalesInsightsAggregate) {
            setLineBarChartDataCompared(
                salesInsightsAggregate.comparedSalesInsightsAggregate.map((salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    [salesIncludingTaxLabel]: salesInsightsAggregateItem.salesInsights.totalSalesIncTax || 0,
                    [netSalesExcludingTaxLabel]: salesInsightsAggregateItem.salesInsights.netSalesExcTax || 0,
                    barData: salesInsightsAggregateItem.salesInsights.transactionVolume || 0
                }))
            );
        }
    }, [salesInsightsAggregate]);

    useEffect(() => {
        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        const compareStart = filters.comparePeriodFrom
            ? filters.comparePeriodFrom
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

        const compareEnd = filters.comparePeriodTo
            ? filters.comparePeriodTo
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;
        const bucket = getBucketFromComparedRanges(
            start,
            end,
            compareStart,
            compareEnd
        );
        setParams(prev => ({
            ...prev,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            compareStartDate: formatDateParam(compareStart),
            compareEndDate: formatDateParam(compareEnd),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket

        }));
    }, [filters, dateRangeConfig]);

    useEffect(() => { fetchSalesAndTransactions(); }, [params, fetchSalesAndTransactions]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesAndTransactions();
        }
    }, [refreshDate, fetchSalesAndTransactions]);

    return (
        <Box sx={{ marginY: '16px' }}>
            <LineBarChartCompared
                title={t('screens.dashboarding.charts.salesIncTaxNetSalesExcTax')}
                currency={salesInsightsAggregate?.currency}
                isError={isError}
                isLoading={isLoading}
                rightAxisLabel={t("screens.dashboarding.charts.volume")}
                leftAxisLabel={t("screens.dashboarding.charts.amount")}
                lineLabels={[
                    t("screens.dashboarding.charts.salesIncludingTax"),
                    t("screens.dashboarding.charts.netSalesExcludingTax")
                ]}
                barLabel={t("screens.dashboarding.charts.volumeOfTransactions")}
                data={lineBarChartData}
                bucket={params.bucket as Bucket}
                setBucket={(value: string) => setParams({
                    ...params,
                    bucket: value as Bucket
                })}
                height={460}
                dataCompared={lineBarChartDataCompared}
                currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
            />
        </Box>
    )
}

export default ComparedSalesIncNetSalesExcTax;

